import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Redo2,
  Underline,
  Undo2,
} from 'lucide-react'
import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { promptModal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { DOC_FONTS } from '../types'

const FONT_SIZES = [9, 10, 11, 12, 14, 16, 18, 20, 24]

type Props = {
  value: string
  onChange: (html: string) => void
  fontFamily: string
  fontSize: number
  lineHeight: number
  onFontFamilyChange: (v: string) => void
  onFontSizeChange: (v: number) => void
  minHeight?: string
  className?: string
  hideToolbar?: boolean
  'aria-label'?: string
}

function runCommand(command: string, value?: string) {
  document.execCommand(command, false, value)
}

export function DocumentRichEditor({
  value,
  onChange,
  fontFamily,
  fontSize,
  lineHeight,
  onFontFamilyChange,
  onFontSizeChange,
  minHeight = '8rem',
  className,
  hideToolbar = false,
  'aria-label': ariaLabel,
}: Props) {
  const { t } = useTranslation()
  const editorRef = useRef<HTMLDivElement>(null)
  const lastEmitted = useRef<string | null>(null)
  const autoId = useId()

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    if (value === lastEmitted.current) return
    if (el.innerHTML === value) return
    el.innerHTML = value || '<p><br/></p>'
    lastEmitted.current = value
  }, [value])

  function emit() {
    const el = editorRef.current
    if (!el) return
    const html = el.innerHTML
    lastEmitted.current = html
    onChange(html)
  }

  function tool(command: string, cmdValue?: string) {
    editorRef.current?.focus()
    runCommand(command, cmdValue)
    emit()
  }

  function insertLink() {
    void (async () => {
      const url = await promptModal({
        title: t('hr.belgeler.editor.linkPrompt'),
        placeholder: 'https://',
        inputType: 'url',
        confirmLabel: t('common.confirm'),
      })
      if (!url?.trim()) return
      tool('createLink', url.trim())
    })()
  }

  return (
    <div className={cn('overflow-hidden rounded-md border border-border bg-card', className)}>
      {!hideToolbar ? (
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/40 px-1.5 py-1"
        role="toolbar"
        aria-label={t('hr.belgeler.editor.toolbar')}
      >
        <Select
          size="sm"
          preserveFocus
          className="w-auto max-w-[9rem]"
          value={fontFamily}
          onChange={(v) => {
            onFontFamilyChange(v)
            tool('fontName', v.split(',')[0]?.replace(/"/g, '') ?? 'Arial')
          }}
          options={DOC_FONTS.map((f) => ({ value: f.value, label: f.label }))}
          aria-label={t('hr.belgeler.editor.font')}
        />
        <Select
          size="sm"
          preserveFocus
          className="w-16"
          value={String(fontSize)}
          onChange={(v) => {
            const n = Number(v)
            onFontSizeChange(n)
            tool('fontSize', '3')
          }}
          options={FONT_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
          aria-label={t('hr.belgeler.editor.fontSize')}
        />
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <ToolButton label={t('hr.belgeler.editor.bold')} onClick={() => tool('bold')}>
          <Bold />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.italic')} onClick={() => tool('italic')}>
          <Italic />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.underline')} onClick={() => tool('underline')}>
          <Underline />
        </ToolButton>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <ToolButton label={t('hr.belgeler.editor.alignLeft')} onClick={() => tool('justifyLeft')}>
          <AlignLeft />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.alignCenter')} onClick={() => tool('justifyCenter')}>
          <AlignCenter />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.alignRight')} onClick={() => tool('justifyRight')}>
          <AlignRight />
        </ToolButton>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <ToolButton label={t('hr.belgeler.editor.bulletList')} onClick={() => tool('insertUnorderedList')}>
          <List />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.numberedList')} onClick={() => tool('insertOrderedList')}>
          <ListOrdered />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.link')} onClick={insertLink}>
          <Link2 />
        </ToolButton>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <ToolButton label={t('hr.belgeler.editor.undo')} onClick={() => tool('undo')}>
          <Undo2 />
        </ToolButton>
        <ToolButton label={t('hr.belgeler.editor.redo')} onClick={() => tool('redo')}>
          <Redo2 />
        </ToolButton>
      </div>
      ) : null}
      <div
        id={autoId}
        ref={editorRef}
        role="textbox"
        aria-multiline
        aria-label={ariaLabel ?? t('hr.belgeler.editor.body')}
        contentEditable
        suppressContentEditableWarning
        className="max-w-none overflow-y-auto scrollbar-none px-3.5 py-3 text-foreground outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30 [&_a]:text-primary [&_a]:underline [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
        style={{ minHeight, fontFamily, fontSize: `${fontSize}px`, lineHeight }}
        onInput={emit}
        onBlur={emit}
      />
    </div>
  )
}

function ToolButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={label} title={label} onClick={onClick}>
      {children}
    </Button>
  )
}
